import React from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, Star } from 'lucide-react';
import Reveal from './Reveal';

/**
 * PackagePreview
 * Compact 3-column preview of the collections for the homepage.
 * Minimal — 4 headline points per package. Full details live on /pricing.
 */

const PACKAGES = [
  {
    id: 'basic',
    name: 'Essential',
    price: 100,
    tagline: 'For smaller celebrations',
    highlights: [
      'Up to 20 photos',
      '1 music track',
      '1-3 days production',
      '1 free revision',
    ],
    popular: false,
  },
  {
    id: 'standard',
    name: 'Signature',
    price: 150,
    tagline: 'Our most popular choice',
    highlights: [
      'Up to 40 photos',
      '2 music tracks',
      '2-4 days production',
      '2 free revisions',
    ],
    popular: true,
  },
  {
    id: 'premium',
    name: 'Cinematic',
    price: 200,
    tagline: 'For weddings & milestones',
    highlights: [
      'Up to 60 photos',
      '3 music tracks',
      '3-5 days production',
      '15-second highlight reel',
    ],
    popular: false,
  },
];

const PackagePreview = () => {
  return (
    <section
      className="py-24 bg-gradient-to-b from-gray-900 to-black"
      data-testid="homepage-package-preview"
    >
      <div className="container mx-auto px-4 lg:px-8">
        <Reveal className="text-center mb-14 max-w-3xl mx-auto">
          <p className="uppercase tracking-[0.3em] text-xs text-amber-400 mb-4">Collections</p>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Three ways to tell{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">
              your story
            </span>
          </h2>
          <p className="text-gray-400 text-base md:text-lg">
            From meaningful moments to milestone weddings - a collection for every occasion.
          </p>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto">
          {PACKAGES.map((pkg, idx) => (
            <Reveal
              key={pkg.id}
              delay={Math.min(idx + 1, 3)}
              className={`relative rounded-2xl p-7 md:p-8 border transition-all duration-300 ${
                pkg.popular
                  ? 'bg-gradient-to-b from-amber-500/10 to-transparent border-amber-500/40 md:scale-[1.03]'
                  : 'bg-white/[0.02] border-white/10 hover:border-amber-500/30'
              }`}
              data-testid={`package-preview-card-${pkg.id}`}
            >
              {pkg.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center gap-1 bg-gradient-to-r from-amber-500 to-amber-600 text-black px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider">
                    <Star size={11} fill="currentColor" /> Most Popular
                  </span>
                </div>
              )}

              <div className="mb-5">
                <h3 className="text-xl font-semibold text-white">{pkg.name}</h3>
                <p className="text-xs text-gray-500 uppercase tracking-wider mt-1">{pkg.tagline}</p>
              </div>

              <div className="mb-6">
                <span className="text-4xl font-bold text-white">£{pkg.price}</span>
              </div>

              <ul className="space-y-2.5 mb-7">
                {pkg.highlights.map((point) => (
                  <li key={point} className="flex items-start gap-2.5">
                    <Check size={16} className="text-amber-400 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                    <span className="text-sm text-gray-300">{point}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/pricing"
                data-testid={`package-preview-cta-${pkg.id}`}
                className={`inline-flex items-center justify-center w-full py-2.5 rounded-full text-sm font-semibold transition-all ${
                  pkg.popular
                    ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-black hover:from-amber-600 hover:to-amber-700'
                    : 'border border-white/15 text-white hover:border-amber-500/50 hover:text-amber-400'
                }`}
              >
                View details
                <ArrowRight size={14} className="ml-1.5" />
              </Link>
            </Reveal>
          ))}
        </div>

        <Reveal className="text-center mt-10">
          <Link
            to="/pricing"
            data-testid="package-preview-compare-link"
            className="inline-flex items-center text-sm text-gray-400 hover:text-amber-400 transition-colors underline underline-offset-4 decoration-white/20 hover:decoration-amber-500/50"
          >
            Compare all features side by side
            <ArrowRight size={14} className="ml-1.5" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

export default PackagePreview;
